import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope, isSimilarKind } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SidePanel } from '../../components/side-panel.js';
import { type DataKind, dataKindsApi, kindLabel } from '../../lib/data-kinds-api.js';
import { mapApiError } from '../../lib/errors.js';
import { useScopedUser } from '../../lib/project-roles.js';

// #47 (Task 9): eigenständige Verwaltung statt CatalogTable-Wiederverwendung — die
// Zusammenführen-Aktion (Ziel-Auswahl aus den ANDEREN Arten) hat kein Äquivalent in
// CatalogTable und würde dort als Fremdkörper wirken. Reiht sich stattdessen ins SidePanel-Muster
// von #37/#45 ein (Titel + Kinder, kein eigenes Overlay-Verhalten).
export function DataKindsPanel({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <SidePanel title={t('catalog.dataKinds.manageTitle')} onClose={onClose}>
      <DataKindsPanelContent projectId={projectId} />
    </SidePanel>
  );
}

function DataKindsPanelContent({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  // #58: Datenarten pflegen (umbenennen/zusammenführen/löschen) ist `project.configure` (Backend
  // erzwingt es zusätzlich). Der Einstiegs-Knopf im Testdaten-Tab ist bereits daran gebunden;
  // diese zweite Absicherung hält die Schreib-Controls auch bei einem Direktrender ehrlich.
  const canConfigure = user ? canInAnyScope(user, 'project.configure') : false;

  const { data: kinds, isLoading } = useQuery({
    queryKey: ['data-kinds', projectId],
    queryFn: () => dataKindsApi.list(projectId),
  });

  // Zusammenführen verschiebt test_data-Zeilen auf die Zielart (Server), Löschen entfernt eine
  // unbenutzte Art — beides macht die bereits geladene Testdaten-Liste veraltet, deshalb beide
  // Query-Keys invalidieren statt nur den eigenen.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['data-kinds', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['testdata', projectId] });
  };

  const updateKind = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { label_de: string | null; label_en: string | null };
    }) => dataKindsApi.update(projectId, id, body),
    meta: { successToast: 'feedback.saved' },
    onSuccess: invalidate,
  });

  const mergeKind = useMutation({
    mutationFn: ({ id, targetId }: { id: string; targetId: string }) =>
      dataKindsApi.merge(projectId, id, targetId),
    meta: { successToast: 'feedback.merged' },
    onSuccess: invalidate,
  });

  // errorToast: false — der 409 (Art noch benutzt) wird namentlich in der Zeile gezeigt, ein
  // globaler Toast wäre die doppelte, generische zweite Meldung (Muster aus #52-Review Finding 1).
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({});
  const removeKind = useMutation({
    mutationFn: (id: string) => dataKindsApi.remove(projectId, id),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onMutate: (id: string) => {
      setDeleteErrors((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    onSuccess: invalidate,
    onError: (err, id) => {
      setDeleteErrors((prev) => ({ ...prev, [id]: mapApiError(t, err) }));
    },
  });

  if (isLoading) return <div className="text-sm text-text-muted">{t('catalog.loading')}</div>;
  const rows = kinds ?? [];
  if (rows.length === 0) return <div className="text-sm text-text-muted">{t('catalog.empty')}</div>;

  return (
    <div className="space-y-3">
      {rows.map((k) => (
        <DataKindRow
          key={k.data_kind_id}
          kind={k}
          others={rows.filter((o) => o.data_kind_id !== k.data_kind_id)}
          canConfigure={canConfigure}
          onSave={(body) => updateKind.mutate({ id: k.data_kind_id, body })}
          onMerge={(targetId) => mergeKind.mutate({ id: k.data_kind_id, targetId })}
          onDelete={() => removeKind.mutate(k.data_kind_id)}
          error={deleteErrors[k.data_kind_id] ?? null}
        />
      ))}
    </div>
  );
}

function DataKindRow({
  kind,
  others,
  canConfigure,
  onSave,
  onMerge,
  onDelete,
  error,
}: {
  kind: DataKind;
  others: DataKind[];
  canConfigure: boolean;
  onSave: (body: { label_de: string | null; label_en: string | null }) => void;
  onMerge: (targetId: string) => void;
  onDelete: () => void;
  error: string | null;
}) {
  const { t, i18n } = useTranslation();
  const [labelDe, setLabelDe] = useState(kind.label_de ?? '');
  const [labelEn, setLabelEn] = useState(kind.label_en ?? '');
  const [target, setTarget] = useState('');

  // #58: Ohne project.configure ist die Zeile rein lesend — nur die Bezeichnung(en) als Text,
  // keine Eingabefelder, kein Speichern/Zusammenführen/Löschen.
  if (!canConfigure) {
    return (
      <div className="space-y-1 rounded-card border border-[var(--border)] p-3">
        <span className="text-sm text-text-primary">
          {kindLabel(kind, i18n.resolvedLanguage ?? 'de')}
        </span>
      </div>
    );
  }

  // Client-Guard spiegelt den Server-Refine `label_required` (catalog-data-kinds.ts): mindestens
  // eine Bezeichnung muss stehen bleiben, sonst würde die Art unbenannt und für niemanden mehr
  // auffindbar (Testdaten-Filter, Ausführungs-Panel).
  const saveDisabled = !labelDe.trim() && !labelEn.trim();

  // #56 Teil 1: Beim UMBENENNEN dieselbe Tippfehler-Prüfung wie beim Anlegen in der Combobox
  // (dort `similar: (typed) => options.find((o) => isSimilarKind(typed, o.label))`). Ohne sie ließe
  // sich der Fast-Duplikat, den die Combobox verhindert, auf dem Umweg "Umbenennen" doch erzeugen.
  // Reine `isSimilarKind`-Regel (Distanz 1..2, exakte Gleichheit zählt nicht); jede Bezeichnung
  // gegen die gleichsprachige der übrigen Arten. Der Reparaturweg ist derselbe: in die getroffene
  // Art zusammenführen (der getippte Name wird verworfen, nicht gespeichert).
  const similarMatch =
    others.find(
      (o) =>
        (o.label_de != null && isSimilarKind(labelDe, o.label_de)) ||
        (o.label_en != null && isSimilarKind(labelEn, o.label_en)),
    ) ?? null;

  return (
    <div className="space-y-2 rounded-card border border-[var(--border)] p-3">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('catalog.fields.nameDe')}
          <input
            value={labelDe}
            onChange={(e) => setLabelDe(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('catalog.fields.nameEn')}
          <input
            value={labelEn}
            onChange={(e) => setLabelEn(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
      </div>

      {/* Warnung VOR den Aktionen und farblich betont — der Blick soll sie erreichen, bevor
          gespeichert wird (Muster aus dem #47-Nutzertest: eine graue Zeile unter dem Speichern
          wird nicht gelesen). Das Angebot führt die aktuelle Art in die getroffene zusammen. */}
      {similarMatch && (
        <div className="flex items-center justify-between gap-2 rounded-btn bg-status-warn/10 px-2 py-1.5 text-xs text-status-warn">
          <span>
            ⚠{' '}
            {t('catalog.dataKinds.similarHint', {
              name: kindLabel(similarMatch, i18n.resolvedLanguage ?? 'de'),
            })}
          </span>
          <button
            type="button"
            onClick={() => onMerge(similarMatch.data_kind_id)}
            className="shrink-0 font-medium underline"
          >
            {t('catalog.dataKinds.merge')}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() =>
            onSave({ label_de: labelDe.trim() || null, label_en: labelEn.trim() || null })
          }
          disabled={saveDisabled}
          title={saveDisabled ? t('catalog.dataKinds.labelRequired') : undefined}
          className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          {t('catalog.actions.save')}
        </button>

        <select
          aria-label={t('catalog.dataKinds.mergeTarget')}
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          disabled={others.length === 0}
          className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-body disabled:opacity-60"
        >
          <option value="">{t('catalog.dataKinds.mergeTarget')}</option>
          {others.map((o) => (
            <option key={o.data_kind_id} value={o.data_kind_id}>
              {kindLabel(o, i18n.resolvedLanguage ?? 'de')}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => target && onMerge(target)}
          disabled={!target}
          className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          {t('catalog.dataKinds.merge')}
        </button>

        <button
          type="button"
          onClick={onDelete}
          className="rounded-btn px-3 py-1.5 text-sm text-status-fail hover:underline"
        >
          {t('catalog.actions.delete')}
        </button>
      </div>

      {error && <div className="text-xs text-status-fail">{error}</div>}
    </div>
  );
}
